import { useCallback, useEffect, useState } from "react";
import { TrendingUp, Loader2, Plus, CheckCircle2, Pause, Play, Trash2, X, Building2, Zap, PiggyBank, Gauge, ShieldAlert, User, Briefcase, RefreshCw, Layers } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { StatePill } from "@/components/membership/Entitlement";
import { UpgradeModal } from "@/components/membership/UpgradeModal";
import { useEntitlements } from "@/hooks/useEntitlements";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

const ICONS = { rent: Building2, bills: Zap, builder_account: PiggyBank, monitoring: Gauge };
const FIELD_META = {
  property_name: { label: "Property / landlord", type: "text" },
  provider_name: { label: "Provider name", type: "text" },
  category: { label: "Category", type: "select", options: ["Utility", "Telecom", "Internet", "Phone", "Insurance"] },
  monthly_amount: { label: "Monthly amount ($)", type: "number" },
  plan_amount: { label: "Monthly contribution ($)", type: "number" },
  term_months: { label: "Term (months)", type: "number" },
};
const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] outline-none focus:border-[#1a5fd0]";

function ProductCell({ p, onUpgrade, onWaitlist, busy }) {
  if (!p) return null;
  const Icon = p.structure === "revolving" ? RefreshCw : Layers;
  return (
    <Card className="card-spotlight flex flex-col p-4" data-testid={`cb-product-${p.product_key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#080d1a] text-white"><Icon size={16} /></span>
          <div>
            <div className="font-head text-[13px] font-bold capitalize text-slate-900">{p.structure}</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">{p.min_plan}+ plan</div>
          </div>
        </div>
        <StatePill state={p.state === "available" ? "included" : p.state} labelOverride={p.state === "available" ? "Available" : undefined} />
      </div>
      <p className="mt-2 flex-1 text-[12px] leading-relaxed text-slate-500">{p.description}</p>
      {p.state === "upgrade_required" && (
        <button onClick={() => onUpgrade(p)} data-testid={`cb-upgrade-${p.product_key}`} className="mt-3 rounded-lg bg-[#b0862f] px-3 py-2 font-head text-[12px] font-bold text-white hover:opacity-90">Upgrade to {p.min_plan}</button>
      )}
      {p.state === "coming_soon" && (p.on_waitlist ? (
        <span className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-600" data-testid={`cb-waitlisted-${p.product_key}`}><CheckCircle2 size={13} /> On the early-access list</span>
      ) : (
        <button onClick={() => onWaitlist(p)} disabled={busy} data-testid={`cb-waitlist-${p.product_key}`} className="mt-3 rounded-lg border border-slate-300 px-3 py-2 font-head text-[12px] font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-60">Join waitlist</button>
      ))}
      {p.state === "available" && Object.keys(p.verified_details || {}).length > 0 && (
        <div className="mt-3 space-y-1 rounded-lg bg-slate-50 p-2 text-[11px] text-slate-600">
          {Object.entries(p.verified_details).map(([k, v]) => <div key={k}><span className="font-mono uppercase text-slate-400">{k.replace(/_/g, " ")}:</span> {v}</div>)}
        </div>
      )}
    </Card>
  );
}

export default function CreditBuilder() {
  const [mk, setMk] = useState(null);
  const [data, setData] = useState(null);
  const [enrollType, setEnrollType] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);
  const { ent, reload: reloadEnt } = useEntitlements();
  const load = useCallback(() => {
    Promise.all([api.get("/credit-builder/marketplace"), api.get("/credit-builder/programs/overview")])
      .then(([m, o]) => { setMk(m.data); setData(o.data); });
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!mk || !data) return <Loading />;

  const enrolledTypes = new Set(data.accounts.filter((a) => a.status === "Active").map((a) => a.type));
  const act = async (fn, ok) => { setBusy(true); try { await fn(); ok && toast.success(ok); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed."); } finally { setBusy(false); } };
  const joinWaitlist = (p) => act(() => api.post("/credit-builder/waitlist", { interest: p.segment, consent: true, product_key: p.product_key, source: "credit_builder_marketplace" }), "You're on the early-access list");
  const submitEnroll = () => act(async () => { await api.post("/credit-builder/programs/enroll", { type: enrollType.type, details: form }); setEnrollType(null); }, `Enrolled in ${enrollType.label}`);

  return (
    <div className="mx-auto max-w-4xl animate-fade-up space-y-8 pb-10" data-testid="credit-builder-page">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight text-slate-900">Credit Builder <span className="text-foil">Marketplace</span></h1>
        <p className="mt-1 max-w-2xl text-[14px] text-slate-500">Personal and business credit-building accounts — revolving and installment — plus reporting programs. Each product shows one honest state: included in your plan, upgrade required, or coming soon with a waitlist. Nothing here is an approval or an offer of credit.</p>
      </div>

      <section data-testid="cb-catalog">
        <h2 className="font-head text-lg font-extrabold text-slate-900">Credit-building accounts</h2>
        <p className="text-[13px] text-slate-500">Personal / Business × Revolving / Installment.</p>
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          {[["personal", User, "Personal"], ["business", Briefcase, "Business"]].map(([seg, Icon, label]) => (
            <div key={seg} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4" data-testid={`cb-segment-${seg}`}>
              <div className="mb-3 flex items-center gap-2 font-head text-[14px] font-bold text-slate-900"><Icon size={15} className="text-[#1a5fd0]" /> {label}</div>
              <div className="grid gap-3">
                <ProductCell p={mk.grid[seg].revolving} onUpgrade={(p) => setModal(p)} onWaitlist={joinWaitlist} busy={busy} />
                <ProductCell p={mk.grid[seg].installment} onUpgrade={(p) => setModal(p)} onWaitlist={joinWaitlist} busy={busy} />
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 flex items-start gap-2 text-[11px] leading-relaxed text-slate-400" data-testid="cb-disclosure"><ShieldAlert size={13} className="mt-0.5 shrink-0" /> {mk.disclosure}</p>
      </section>

      <section data-testid="cb-programs">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="font-head text-lg font-extrabold text-slate-900">Reporting programs</h2>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-700" data-testid="beta-badge">BETA</span>
        </div>
        <p className="text-[13px] text-slate-500">{mk.programs_note}</p>
        <div className="mt-3 grid gap-3 sm:grid-cols-3">
          <Card className="p-4"><div className="font-mono text-[10px] uppercase tracking-wider text-slate-400">Readiness boost</div><div className="mt-1 font-head text-2xl font-extrabold text-emerald-600" data-testid="cb-boost">+{data.readiness_boost} pts</div></Card>
          <Stat label="On-time payments" value={data.stats.on_time_payments} tone="blue" />
          <Stat label="Active programs" value={data.stats.active} />
        </div>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {mk.programs.map((p) => {
            const Icon = ICONS[p.type] || TrendingUp;
            const active = enrolledTypes.has(p.type);
            return (
              <Card key={p.type} className="flex flex-col p-4" data-testid={`program-card-${p.type}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#1a5fd0]/10 text-[#1a5fd0]"><Icon size={19} /></span>
                    <div><div className="font-head text-[14px] font-bold text-slate-900">{p.label}</div><div className="font-mono text-[10px] text-slate-400">{p.min_plan}+ · reports to {p.bureaus.join(", ")}</div></div>
                  </div>
                  <StatePill state={p.state} />
                </div>
                <p className="mt-2 flex-1 text-[12px] leading-relaxed text-slate-500">{p.desc}</p>
                {p.state === "upgrade_required" ? (
                  <button onClick={() => setModal({ product_key: "credit_builder", min_plan: p.min_plan })} data-testid={`upgrade-${p.type}`} className="mt-3 rounded-lg bg-[#b0862f] px-3 py-2 font-head text-[12px] font-bold text-white hover:opacity-90">Upgrade to {p.min_plan}</button>
                ) : (
                  <button onClick={() => { setEnrollType(p); setForm({}); }} disabled={active} data-testid={`enroll-${p.type}`} className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#080d1a] px-3 py-2 text-[13px] font-bold text-white hover:bg-[#1a5fd0] disabled:bg-slate-200 disabled:text-slate-400">
                    {active ? <><CheckCircle2 size={14} /> Enrolled</> : <><Plus size={14} /> Enroll</>}
                  </button>
                )}
              </Card>
            );
          })}
        </div>
      </section>

      {data.accounts.length > 0 && (
        <section>
          <h2 className="mb-2 font-head text-lg font-extrabold text-slate-900">My programs</h2>
          <div className="space-y-3">
            {data.accounts.map((a) => (
              <Card key={a.id} className="p-4" data-testid="my-program-row">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2"><span className="font-head text-[14px] font-bold text-slate-900">{a.label}</span><span className={`rounded-full px-2 py-0.5 font-mono text-[9px] font-bold uppercase ${a.status === "Active" ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}>{a.status}</span></div>
                    <div className="mt-0.5 font-mono text-[10px] text-slate-400">{a.ontime_count} on-time · reporting: <span className="text-amber-600">Awaiting reporting provider</span></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => act(() => api.post(`/credit-builder/programs/${a.id}/pay`), "On-time payment logged")} disabled={a.paid_this_month || a.status !== "Active"} data-testid="pay-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-[12px] font-bold text-white hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-400"><CheckCircle2 size={13} /> {a.paid_this_month ? "Paid this month" : "Log payment"}</button>
                    <button onClick={() => act(() => api.patch(`/credit-builder/programs/${a.id}`, { status: a.status === "Active" ? "Paused" : "Active" }))} className="text-slate-400 hover:text-slate-700">{a.status === "Active" ? <Pause size={15} /> : <Play size={15} />}</button>
                    <button onClick={() => act(() => api.delete(`/credit-builder/programs/${a.id}`), "Program closed")} className="text-slate-300 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {enrollType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="enroll-modal">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setEnrollType(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="mb-1 flex items-center justify-between"><h3 className="font-head text-lg font-extrabold text-slate-900">Enroll — {enrollType.label}</h3><button onClick={() => setEnrollType(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
            <p className="mb-4 text-[12px] text-amber-700">BETA · On-time payments boost your readiness now; bureau reporting activates once a provider is connected.</p>
            <div className="space-y-3">
              {(enrollType.fields || []).map((f) => {
                const meta = FIELD_META[f] || { label: f, type: "text" };
                return (
                  <label key={f} className="block"><span className="mb-1 block font-mono text-[10px] uppercase tracking-wider text-slate-400">{meta.label}</span>
                    {meta.type === "select" ? (
                      <select value={form[f] || ""} onChange={(e) => setForm({ ...form, [f]: e.target.value })} data-testid={`field-${f}`} className={inputCls}><option value="">Select…</option>{meta.options.map((o) => <option key={o}>{o}</option>)}</select>
                    ) : (
                      <input type={meta.type} value={form[f] || ""} onChange={(e) => setForm({ ...form, [f]: meta.type === "number" ? Number(e.target.value) : e.target.value })} data-testid={`field-${f}`} className={inputCls} />
                    )}
                  </label>
                );
              })}
              {(!enrollType.fields || enrollType.fields.length === 0) && <p className="text-[13px] text-slate-500">No details needed — this program monitors your file. Click enroll to activate.</p>}
            </div>
            <button onClick={submitEnroll} disabled={busy} data-testid="enroll-submit" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#080d1a] py-2.5 text-[14px] font-bold text-white hover:bg-[#1a5fd0] disabled:opacity-60">{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Enroll</button>
          </div>
        </div>
      )}
      {modal && ent && <UpgradeModal open onClose={() => { setModal(null); reloadEnt(); load(); }} feature="credit_builder" requiredPlan={modal.min_plan} returnRoute="/credit-builder" ent={ent} />}
    </div>
  );
}
